import {
  Button,
  Dialog,
  DialogContent,
  InfoTooltip,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@ais-ui/ui-core';
import {
  ShipInfo,
  TargetInfoType,
  TargetTypeEnum,
  useDownloadTrack,
  useSession,
} from '@ais-ui/websockets';
import { useState } from 'react';
import { PiBoatThin } from 'react-icons/pi';
import { ShowShip } from './ShowShip';

const ActionButton = ({
  id,
  Icon,
  disabled,
  onClick,
  text,
  disabledText,
  ...rest
}: {
  id: string;
  Icon: React.ElementType;
  disabled?: boolean;
  onClick?: () => void;
  text: string;
  disabledText?: string;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const onMouseEnter = () => {
    if (disabled) {
      setIsHovered(true);
    }
  };

  return (
    <div
      onMouseEnter={() => onMouseEnter()}
      onMouseLeave={() => setIsHovered(false)}
      className="col-span-2"
    >
      <Button
        id={id}
        disabled={disabled}
        variant="ghost"
        onClick={onClick}
        {...rest}
        className="flex gap-x-2 col-span-2 justify-start"
      >
        <Icon />
        {text}
      </Button>
      {disabled && isHovered && (
        <InfoTooltip className="-bottom-2">{disabledText}</InfoTooltip>
      )}
    </div>
  );
};

interface TargetActionsProps {
  targetInfo: TargetInfoType;
  targetType: TargetTypeEnum;
}

export const TargetActions = ({
  targetInfo,
  targetType,
}: TargetActionsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isShowVesselActive, setIsShowVesselActive] = useState<boolean>(false);
  const { olSession } = useSession();

  const { startDownload } = useDownloadTrack(olSession);

  const onShowVesselClick = () => {
    setIsShowVesselActive(!isShowVesselActive);
  };

  const onDownloadTrackClick = () => {
    if (targetType === TargetTypeEnum.Ship) {
      startDownload(Number((targetInfo as ShipInfo).mmsi));
    }
  };

  if (
    targetType !== TargetTypeEnum.Ship &&
    targetType !== TargetTypeEnum.ExtendedVessel
  ) {
    return null;
  }

  return (
    <>
      <div className="flex w-full justify-end">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button id="ship-actions-button">Actions</Button>
          </PopoverTrigger>
          <PopoverContent className="w-96" side="left" align="end">
            <div className="grid gap-4">
              <div className="space-y-2">
                <h4 className="font-medium leading-none">Actions</h4>
                <p className="text-sm text-muted-foreground">
                  Take one of the following actions
                </p>
              </div>
              <div className="grid gap-2"></div>
            </div>
            <div className="grid grid-cols-4">
              {(targetType === TargetTypeEnum.Ship ||
                targetType === TargetTypeEnum.ExtendedVessel) && (
                <ActionButton
                  id="show-vessel-button"
                  Icon={PiBoatThin}
                  onClick={() => onShowVesselClick()}
                  text="show ship image"
                ></ActionButton>
              )}
              {targetType === TargetTypeEnum.Ship && (
                <ActionButton
                  id="download-track-button"
                  Icon={PiBoatThin}
                  onClick={() => onDownloadTrackClick()}
                  text="download track"
                ></ActionButton>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <Dialog open={isShowVesselActive} onOpenChange={setIsShowVesselActive}>
        <DialogContent>
          <ShowShip
            shipInfo={targetInfo as ShipInfo}
            setIsShowVesselActive={setIsShowVesselActive}
          ></ShowShip>
        </DialogContent>
      </Dialog>
    </>
  );
};
